import React from 'react';
import { withRouter } from "react-router-dom";
import compose from 'recompose/compose';
import withStyles from '@material-ui/core/styles/withStyles';
import Paper from '@material-ui/core/Paper';
import Alert from './Alert';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import SaveIcon from '@material-ui/icons/Save';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import TablePagination from '@material-ui/core/TablePagination';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import Auth from '../services/auth';
import DistributionDataAccess from '../services/distributionDataAccess';

const auth = new Auth();
const distAccess = new DistributionDataAccess();

const styles = theme => ({
    paper: {
        padding: theme.spacing(2),
    },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1)
    },
    input: {
        width: '100%'
    },
    actions: {
        display: 'flex',
        gap: theme.spacing(1)
    },
    topControls: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1)
    },
    searchField: {
        maxWidth: 400
    }
});

class DistributionsManager extends React.Component {
    state = {
        showAlert: false,
        loading: true,
        distributions: [],
        editId: null,
        edited: {},
        newRow: { Name: '', Address: '', Account: auth.getActiveAccount() },
        saving: false,
        snackOpen: false,
        snackMessage: '',
        // pagination/search
        searchTerm: '',
        page: 0,
        rowsPerPage: 10,
        alert: {
            alertText: "",
            alertOkText: "",
            alertCancelText: "",
            alertTitle: "",
            onAlertOk: () => { },
            onAlertCancel: () => this.setState({ showAlert: false }),
            component: null

        },
    };

    hasDuplicateAddress = (address, excludeId) => {
        if (!address) return false;
        const norm = address.trim().toLowerCase();
        return this.state.distributions.some(d => {
            const rowId = d.Id || d.id;
            if (excludeId && rowId === excludeId) return false;
            return (d.Address || '').trim().toLowerCase() === norm;
        });
    };

    componentDidMount() {
        this.load();
    }

    load = async () => {
        this.setState({ loading: true });
        try {
            const account = auth.getActiveAccount();
            const list = await distAccess.getByAccount(account);
            const normalized = (Array.isArray(list) ? list : []).map(d => ({ ...d, Account: d.Account || account }));
            this.setState({ distributions: normalized, loading: false });
        } catch (err) {
            this.setState({ loading: false, snackOpen: true, snackMessage: 'Failed to load distributions' });
        }
    };

    startEdit = (row) => {
        const normalizedId = row.Id || row.id;
        this.setState({ editId: normalizedId, edited: { ...row, Id: normalizedId } });
    };

    cancelEdit = () => {
        this.setState({ editId: null, edited: {} });
    };

    updateField = (field, value) => {
        this.setState(prev => ({ edited: { ...prev.edited, [field]: value } }));
    };

    saveEdited = async () => {
        const { edited } = this.state;
        this.setState({ saving: true });
        try {
            // Prevent duplicate addresses (exclude current edited row)
            const editedId = edited.Id || edited.id;
            if (this.hasDuplicateAddress(edited.Address, editedId)) {
                this.setState({ saving: false, snackOpen: true, snackMessage: 'A distribution with that address already exists.' });
                return;
            }

            const payload = { ...edited, Id: editedId, Account: edited.Account || auth.getActiveAccount() };
            await distAccess.saveDistribution(payload);
            await this.load();
            this.setState({ editId: null, edited: {}, saving: false, snackOpen: true, snackMessage: 'Saved' });
        } catch (err) {
            this.setState({ saving: false, snackOpen: true, snackMessage: 'Save failed' });
        }
    };

    // Delete a distribution. If the distribution belongs to another account,
    // use the rowAccount passed in (if provided) so the correct account is used.
    deleteRow = async (id, rowAccount) => {
        this.setState({
            showAlert: true,
            alert: {
                alertTitle: "Delete Distribution",
                alertText: "Are you sure you want to delete this distribution?",
                alertOkText: "DELETE",
                alertCancelText: "CANCEL",
                onAlertOk: () => {
                    this.confirmDeleteRow(id, rowAccount);
                    this.setState({ showAlert: false });
                },
                onAlertCancel: () => {
                    this.setState({ showAlert: false });
                },
                component: null
            }
        });
    };

    confirmDeleteRow = async (id, rowAccount) => {
        try {
            const account = rowAccount || auth.getActiveAccount();
            this.setState({ showAlert: false });

            await distAccess.deleteDistribution(account, id);

            this.setState({
                openSnack: true,
                snackMessage: "Distribution deleted successfully"
            });

            await this.load();

        } catch (error) {
            this.setState({
                openSnack: true,
                snackMessage: "Failed to delete distribution"
            });
        }
    };

    updateNewField = (field, value) => {
        this.setState(prev => ({ newRow: { ...prev.newRow, [field]: value } }));
    };

    addNew = async () => {
        const { newRow } = this.state;
        if (!newRow.Name || !newRow.Address) {
            this.setState({ snackOpen: true, snackMessage: 'Name and Address required' });
            return;
        }
        this.setState({ saving: true });
        try {
            // Prevent duplicate addresses (exclude current edited row)
            if (this.hasDuplicateAddress(newRow.Address, newRow.Id)) {
                this.setState({ saving: false, snackOpen: true, snackMessage: 'A distribution with that address already exists.' });
                return;
            }
            const payload = { ...newRow, Account: newRow.Account || auth.getActiveAccount() };
            await distAccess.saveDistribution(payload);
            await this.load();
            this.setState({ newRow: { Name: '', Address: '', Account: auth.getActiveAccount() }, saving: false, snackOpen: true, snackMessage: 'Added', page: 0 });
        } catch (err) {
            this.setState({ saving: false, snackOpen: true, snackMessage: 'Add failed' });
        }
    };

    handleSnackClose = () => this.setState({ snackOpen: false, snackMessage: '' });

    // search & pagination handlers
    handleSearchChange = (e) => {
        this.setState({ searchTerm: e.target.value.toLowerCase(), page: 0 });
    };

    handleChangePage = (event, newPage) => {
        this.setState({ page: newPage });
    };

    handleChangeRowsPerPage = (event) => {
        this.setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0 });
    };

    filteredDistributions = () => {
        const { distributions, searchTerm } = this.state;
        if (!searchTerm) return distributions;
        return distributions.filter(d =>
            (d.Name || '').toLowerCase().includes(searchTerm) ||
            (d.Address || '').toLowerCase().includes(searchTerm)
        );
    };

    render() {
        const { classes } = this.props;
        const { loading, editId, edited, newRow, saving, snackOpen, snackMessage, searchTerm, page, rowsPerPage } = this.state;

        const filtered = this.filteredDistributions();
        const emptyRows = rowsPerPage - Math.min(rowsPerPage, filtered.length - page * rowsPerPage);
        const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        return (
            <Paper className={classes.paper}>
                <div className={classes.headerRow}>
                    <Typography variant="h6">Manage Distributions</Typography>
                    <div>
                        <Button variant="outlined" onClick={() => this.props.history.goBack()}>Close</Button>
                    </div>
                </div>

                <div className={classes.topControls}>
                    <TextField
                        className={classes.searchField}
                        placeholder="Search by name, address"
                        value={searchTerm}
                        onChange={this.handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>

                {loading && <CircularProgress />}

                {!loading && (
                    <div>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Address (fax/email)</TableCell>
                                    {/*<TableCell>Account</TableCell>*/}
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* inline add row */}
                                <TableRow>
                                    <TableCell>
                                        <TextField value={newRow.Name} onChange={e => this.updateNewField('Name', e.target.value)} placeholder="Name" className={classes.input} />
                                    </TableCell>
                                    <TableCell>
                                        <TextField value={newRow.Address} onChange={e => this.updateNewField('Address', e.target.value)} placeholder="Fax or Email" className={classes.input} />
                                    </TableCell>
                                    {/*<TableCell>*/}
                                    {/*    <TextField value={newRow.Account} onChange={e => this.updateNewField('Account', e.target.value)} className={classes.input} />*/}
                                    {/*</TableCell>*/}
                                    <TableCell>
                                        <div className={classes.actions}>
                                            <Button variant="contained" color="primary" size="small" onClick={this.addNew} startIcon={<AddIcon />} disabled={saving}>Add</Button>
                                        </div>
                                    </TableCell>
                                </TableRow>

                                {paged.map(row => (
                                    <TableRow key={row.Id || `${row.Name}-${row.Address}`}>
                                        <TableCell>
                                            {editId === row.Id ? (
                                                <TextField value={edited.Name || ''} onChange={e => this.updateField('Name', e.target.value)} className={classes.input} />
                                            ) : (
                                                row.Name
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editId === row.Id ? (
                                                <TextField value={edited.Address || ''} onChange={e => this.updateField('Address', e.target.value)} className={classes.input} />
                                            ) : (
                                                row.Address
                                            )}
                                        </TableCell>
                                        {/*<TableCell>*/}
                                        {/*    {editId === row.Id ? (*/}
                                        {/*        <TextField value={edited.Account || ''} onChange={e => this.updateField('Account', e.target.value)} />*/}
                                        {/*    ) : (*/}
                                        {/*        row.Account*/}
                                        {/*    )}*/}
                                        {/*</TableCell>*/}
                                        <TableCell>
                                            {editId === row.Id ? (
                                                <div className={classes.actions}>
                                                    <Button size="small" color="primary" variant="contained" onClick={this.saveEdited} startIcon={<SaveIcon />} disabled={saving}>Save</Button>
                                                    <Button size="small" onClick={this.cancelEdit} disabled={saving}>Cancel</Button>
                                                </div>
                                            ) : (
                                                <div className={classes.actions}>
                                                    <IconButton size="small" onClick={() => this.startEdit(row)} aria-label="edit"><EditIcon /></IconButton>
                                                        <IconButton size="small" onClick={() => this.deleteRow(row.Id, row.Account || row.account)} aria-label="delete"><DeleteIcon /></IconButton>
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {emptyRows > 0 && Array.from(Array(emptyRows)).map((_, idx) => (
                                    <TableRow key={`empty-${idx}`} style={{ height: 49 }}>
                                        <TableCell colSpan={4} />
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <TablePagination
                            component="div"
                            count={filtered.length}
                            page={page}
                            onPageChange={this.handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </div>
                )}
                <Alert
                    showAlert={this.state.showAlert}
                    alertOkText={this.state.alert.alertOkText}
                    alertCancelText={this.state.alert.alertCancelText}
                    alertText={this.state.alert.alertText}
                    alertTitle={this.state.alert.alertTitle}
                    onAlertOk={this.state.alert.onAlertOk}
                    onAlertCancel={this.state.alert.onAlertCancel}
                >{this.state.alert.component}</Alert>
                <Snackbar
                    open={snackOpen}
                    autoHideDuration={3000}
                    onClose={this.handleSnackClose}
                    message={snackMessage}
                />
            </Paper>
        );
    }
}

export default compose(withRouter, withStyles(styles))(DistributionsManager);


// WEBPACK FOOTER //
// ./src/components/DistributionsManager.js